import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { Breadcrumb } from "@/components/service/ServiceHero";
import { scrollToId } from "@/components/site/SectionLink";
import { QuoteForm } from "@/components/site/QuoteForm";
import { TRUST_POINTS } from "@/components/site/Trust";
import { SERVICES, PHONE_DISPLAY, PHONE_HREF } from "@/components/site/data";
import { findTown } from "@/components/site/areaContent";

export const AreasCrumb = () => <Link to="/areas" className="hover:text-brand-blue" data-testid="breadcrumb-areas">Service Areas</Link>;

export const AreaHero = ({ town }) => (
  <section data-testid="area-hero" className="bg-white">
    <div className="mx-auto max-w-7xl px-5 pt-8 sm:px-8">
      <Breadcrumb parent={<AreasCrumb />} title={town.name} />
    </div>
    <div className="mx-auto max-w-7xl px-5 pb-16 pt-10 sm:px-8 lg:pb-20 lg:pt-14">
      <div className="max-w-4xl">
      <p className="eyebrow inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Serving {town.name}{town.inChathamKent === false ? "" : ", Chatham-Kent"}</p>
      <h1 data-testid="area-hero-heading" className="mt-5 font-heading text-4xl font-semibold leading-[1.12] tracking-tight text-brand-navy sm:text-5xl">{town.h1}</h1>
      <p data-testid="area-hero-intro" className="mt-6 max-w-2xl text-base leading-relaxed text-brand-slate md:text-lg">{town.intro}</p>
      <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <a href="#quote" onClick={(e) => { e.preventDefault(); scrollToId("quote"); }} data-testid="area-hero-quote-button" className="btn-primary">
          Request a Quote <ArrowRight className="h-4 w-4" />
        </a>
        <a href={PHONE_HREF} data-testid="area-hero-call-button" className="btn-secondary">
          <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
        </a>
      </div>
      </div>
    </div>
  </section>
);

export const AreaServices = ({ town }) => (
  <section data-testid="area-services-section" className="border-t border-brand-surface bg-brand-offwhite py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="eyebrow">Our Services</p>
      <h2 className="h2 mt-4">Services in {town.name}</h2>
      <p className="lead mt-4 max-w-2xl">{town.servicesIntro}</p>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-brand-slate" data-testid="area-services-note">{town.servicesNote}</p>
      <ul className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map(({ id, slug, title, desc, icon: Icon }) => (
          <li key={id}>
            <Link to={`/services/${slug}`} data-testid={`area-service-link-${id}`} className="soft-card flex h-full flex-col p-6 transition-shadow duration-300 hover:shadow-md">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-offwhite"><Icon className="h-6 w-6 text-brand-blue" strokeWidth={1.6} /></span>
              <h3 className="mt-4 font-heading text-base font-semibold text-brand-navy">{title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-slate">{desc}</p>
              <span className="link-arrow mt-4">Learn More <ArrowRight className="h-4 w-4" /></span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export const AreaUseCases = ({ town }) => (
  <section data-testid="area-usecases-section" className="bg-white py-20 lg:py-24">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
      <div>
        <p className="eyebrow">Local Businesses</p>
        <h2 className="h2 mt-4">{town.businessesTitle}</h2>
        <p className="lead mt-5">{town.businessesText}</p>
        <p className="mt-4 text-sm leading-relaxed text-brand-slate" data-testid="area-local-note">{town.localNote}</p>
      </div>
      <ul className="grid gap-5 sm:grid-cols-2">
        {town.useCases.map((u, i) => (
          <li key={u.title} data-testid={`area-usecase-${i}`} className="rounded-xl border border-brand-surface bg-brand-offwhite p-6">
            <h3 className="font-heading text-base font-semibold text-brand-navy">{u.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-brand-slate">{u.desc}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export const AreaWhy = () => (
  <section data-testid="area-why-section" className="bg-brand-navy py-20 text-white lg:py-24">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="eyebrow eyebrow-light">Why JMS</p>
      <h2 className="mt-4 font-heading text-[1.75rem] font-semibold leading-tight tracking-tight sm:text-4xl">Why Work With JMS</h2>
      <ul className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {TRUST_POINTS.map(({ icon: Icon, title, desc }, i) => (
          <li key={title} data-testid={`area-why-${i}`} className="rounded-xl border border-white/10 bg-brand-deep p-6">
            <Icon className="h-6 w-6 text-brand-cyan" strokeWidth={1.6} />
            <h3 className="mt-4 font-heading text-base font-semibold">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/70">{desc}</p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export const AreaQuote = ({ town }) => (
  <section id="quote" data-testid="area-quote-section" className="bg-brand-offwhite py-20 lg:py-24">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
      <div className="lg:pt-6">
        <p className="eyebrow">Request a Quote</p>
        <h2 className="h2 mt-5">Request a Quote in {town.name}</h2>
        <p className="lead mt-4 max-w-md">Tell us about your {town.name} business and what you need. We'll follow up with clear next steps.</p>
        <a href={PHONE_HREF} data-testid="area-quote-call-button" className="btn-secondary mt-8">
          <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
        </a>
      </div>
      <QuoteForm town={town.name} />
    </div>
  </section>
);

export const NearbyAreas = ({ town }) => (
  <section data-testid="nearby-areas-section" className="bg-white py-16 lg:py-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <h2 className="font-heading text-2xl font-semibold tracking-tight text-brand-navy">Nearby Areas We Serve</h2>
      <ul className="mt-8 flex flex-wrap gap-3">
        {town.nearby.map(findTown).map((t) => (
          <li key={t.slug}>
            <Link to={`/areas/${t.slug}`} data-testid={`nearby-area-link-${t.slug}`} className="inline-flex items-center gap-2 rounded-full border border-brand-blue/20 bg-white px-4 py-2.5 text-sm font-medium text-brand-navy transition-colors hover:border-brand-blue hover:text-brand-blue">
              <MapPin className="h-4 w-4 text-brand-blue" strokeWidth={1.75} /> {t.name}
            </Link>
          </li>
        ))}
        <li>
          <Link to="/areas" data-testid="nearby-area-link-hub" className="inline-flex items-center gap-2 rounded-full border border-brand-navy px-4 py-2.5 text-sm font-medium text-brand-navy transition-colors hover:bg-brand-navy hover:text-white">
            All service areas <ArrowRight className="h-4 w-4" />
          </Link>
        </li>
      </ul>
    </div>
  </section>
);
